import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  Palette as StyleIcon,
  RestartAlt as ResetIcon,
  ZoomInMap as ZoomIcon,
} from '@mui/icons-material';
import type { ReferenceLayersController, ReferenceLayerView } from '../../hooks/useReferenceLayers';
import {
  REFERENCE_COLOR_RAMPS,
  REFERENCE_LAYER_TYPE_LABELS,
  geometryKindLabel,
  type ReferenceFieldStat,
  type ReferenceGeometryKind,
  type ReferenceLayerDto,
  type ReferenceLayerStyle,
} from '../../services/referenceLayerApi';
import { referenceLegend } from './ReferenceLayersMapLayers';

export type ReferenceLayersSectionProps = {
  controller: ReferenceLayersController;
  /** Fly the map to a layer's bbox. */
  onZoomTo?: (bbox: [number, number, number, number]) => void;
  /** Hide the uppercase section heading (when the host renders its own). */
  hideTitle?: boolean;
  /** Shown when there are no layers (admins see a hint about where to upload). */
  isAdmin?: boolean;
};

const PCT = (v: number) => `${Math.round(v * 100)}%`;

/** Small colour input that looks like a swatch. */
const Swatch: React.FC<{ value: string; onChange: (hex: string) => void; label: string }> = ({ value, onChange, label }) => (
  <Tooltip title={label}>
    <Box
      component="label"
      sx={{
        width: 22,
        height: 22,
        borderRadius: '6px',
        bgcolor: value,
        border: '1px solid rgba(255,255,255,0.35)',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.35) inset',
        cursor: 'pointer',
        position: 'relative',
        flexShrink: 0,
      }}
    >
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
      />
    </Box>
  </Tooltip>
);

const RampPreview: React.FC<{ colors: string[]; reverse?: boolean; width?: number }> = ({ colors, reverse, width = 56 }) => {
  const list = reverse ? [...colors].reverse() : colors;
  return (
    <Box
      sx={{
        width,
        height: 10,
        borderRadius: 1,
        background: `linear-gradient(90deg, ${list.join(', ')})`,
        border: '1px solid rgba(255,255,255,0.2)',
        flexShrink: 0,
      }}
    />
  );
};

export type ReferenceStyleControlsProps = {
  geometryKind: ReferenceGeometryKind;
  fields: Record<string, ReferenceFieldStat>;
  labelField?: string | null;
  style: ReferenceLayerStyle;
  onChange: (patch: Partial<ReferenceLayerStyle>) => void;
};

/**
 * Colour / opacity / outline / colour-ramp / label controls for one layer.
 * Shared by the user-facing settings dialogs and the admin import dialog.
 */
export const ReferenceStyleControls: React.FC<ReferenceStyleControlsProps> = ({ geometryKind, fields, labelField, style, onChange }) => {
  const kind = geometryKind;
  const hasFill = kind === 'polygon' || kind === 'mixed';
  const hasStroke = kind !== 'point';
  const hasPoints = kind === 'point' || kind === 'mixed';
  const fieldEntries = useMemo(
    () =>
      Object.entries(fields || {}).filter(
        ([, s]) => (s.kind === 'number' && s.min != null && s.max != null && s.max > s.min) || (s.values && s.values.length > 1)
      ),
    [fields]
  );
  const colorBy = style.color_by;
  const selectedStat = colorBy ? fields[colorBy.field] : undefined;
  const canRamp = Boolean(selectedStat && selectedStat.kind === 'number' && selectedStat.min != null && selectedStat.max != null);
  const canCategorical = Boolean(selectedStat?.values && selectedStat.values.length > 0);
  const legend = useMemo(
    () => referenceLegend({ fields, label_field: labelField } as unknown as ReferenceLayerDto, style),
    [fields, labelField, style]
  );
  const layer = { fields, label_field: labelField ?? null };

  return (
      <Stack spacing={1}>
        {/* Colours */}
        <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Swatch value={style.color} onChange={(hex) => onChange({ color: hex })} label={hasPoints && !hasFill ? 'Point colour' : 'Fill colour'} />
            <Typography variant="caption" color="text.secondary">
              {hasPoints && !hasFill ? 'Points' : 'Fill'}
            </Typography>
          </Stack>
          {hasStroke && (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Swatch value={style.stroke_color} onChange={(hex) => onChange({ stroke_color: hex })} label="Outline colour" />
              <Typography variant="caption" color="text.secondary">
                Outline
              </Typography>
            </Stack>
          )}
          <FormControlLabel
            sx={{ m: 0, ml: 'auto' }}
            control={
              <Switch
                size="small"
                checked={style.labels}
                disabled={!layer.label_field}
                onChange={(e) => onChange({ labels: e.target.checked })}
              />
            }
            label={
              <Tooltip title={layer.label_field ? `Label by "${layer.label_field}"` : 'No label field set by the admin'}>
                <Typography variant="caption">Labels</Typography>
              </Tooltip>
            }
          />
        </Stack>

        {/* Opacity / size sliders */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, rowGap: 0.25 }}>
          {hasFill && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Fill opacity · {PCT(style.fill_opacity)}
              </Typography>
              <Slider size="small" min={0} max={1} step={0.05} value={style.fill_opacity} onChange={(_, v) => onChange({ fill_opacity: v as number })} />
            </Box>
          )}
          {hasStroke && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Outline · {style.stroke_width.toFixed(1)} px · {PCT(style.stroke_opacity)}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Slider size="small" min={0} max={8} step={0.5} value={style.stroke_width} onChange={(_, v) => onChange({ stroke_width: v as number })} />
                <Slider size="small" min={0} max={1} step={0.05} value={style.stroke_opacity} onChange={(_, v) => onChange({ stroke_opacity: v as number })} />
              </Stack>
            </Box>
          )}
          {hasPoints && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Points · {style.point_radius} px · {PCT(style.point_opacity)}
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Slider size="small" min={1} max={20} step={1} value={style.point_radius} onChange={(_, v) => onChange({ point_radius: v as number })} />
                <Slider size="small" min={0} max={1} step={0.05} value={style.point_opacity} onChange={(_, v) => onChange({ point_opacity: v as number })} />
              </Stack>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Show from zoom · {style.min_zoom === 0 ? 'always' : style.min_zoom}
            </Typography>
            <Slider size="small" min={0} max={16} step={1} value={style.min_zoom} onChange={(_, v) => onChange({ min_zoom: v as number })} />
          </Box>
        </Box>

        {/* Colour by property */}
        {fieldEntries.length > 0 && (
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Colour by</InputLabel>
              <Select
                label="Colour by"
                value={colorBy?.field ?? ''}
                sx={{ height: 32, fontSize: '0.78rem' }}
                onChange={(e) => {
                  const field = e.target.value as string;
                  if (!field) {
                    onChange({ color_by: null });
                    return;
                  }
                  const stat = layer.fields[field];
                  const mode = stat?.kind === 'number' && stat.min != null && stat.max != null ? 'ramp' : 'categorical';
                  onChange({ color_by: { field, mode, ramp: colorBy?.ramp || 'viridis', reverse: colorBy?.reverse || false } });
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 260 } } }}
              >
                <MenuItem value="" sx={{ fontSize: '0.78rem' }}>
                  <em>Single colour</em>
                </MenuItem>
                {fieldEntries.map(([name, s]) => (
                  <MenuItem key={name} value={name} sx={{ fontSize: '0.78rem' }}>
                    {name}
                    <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.75 }}>
                      {s.kind === 'number' ? 'number' : `${s.distinct} values`}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {colorBy && canRamp && canCategorical && (
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Mode</InputLabel>
                <Select
                  label="Mode"
                  value={colorBy.mode}
                  sx={{ height: 32, fontSize: '0.78rem' }}
                  onChange={(e) => onChange({ color_by: { ...colorBy, mode: e.target.value as 'ramp' | 'categorical' } })}
                >
                  <MenuItem value="ramp" sx={{ fontSize: '0.78rem' }}>Ramp</MenuItem>
                  <MenuItem value="categorical" sx={{ fontSize: '0.78rem' }}>Categories</MenuItem>
                </Select>
              </FormControl>
            )}
            {colorBy && colorBy.mode === 'ramp' && canRamp && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.75rem' }}>Ramp</InputLabel>
                <Select
                  label="Ramp"
                  value={colorBy.ramp}
                  sx={{ height: 32, fontSize: '0.78rem' }}
                  onChange={(e) => onChange({ color_by: { ...colorBy, ramp: e.target.value as string } })}
                  renderValue={(v) => (
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <RampPreview colors={REFERENCE_COLOR_RAMPS[v as string] || []} reverse={colorBy.reverse} width={44} />
                      <span>{v as string}</span>
                    </Stack>
                  )}
                >
                  {Object.entries(REFERENCE_COLOR_RAMPS).map(([name, colors]) => (
                    <MenuItem key={name} value={name} sx={{ fontSize: '0.78rem', gap: 1 }}>
                      <RampPreview colors={colors} reverse={colorBy.reverse} />
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {colorBy && (
              <FormControlLabel
                sx={{ m: 0 }}
                control={<Switch size="small" checked={colorBy.reverse} onChange={(e) => onChange({ color_by: { ...colorBy, reverse: e.target.checked } })} />}
                label={<Typography variant="caption">Reverse</Typography>}
              />
            )}
          </Stack>
        )}

        {legend.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {legend.slice(0, 24).map((item) => (
              <Chip
                key={`${item.color}-${item.label}`}
                size="small"
                label={item.label}
                sx={{
                  height: 20,
                  fontSize: '0.64rem',
                  '& .MuiChip-label': { px: 0.75 },
                  bgcolor: 'transparent',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&::before': {
                    content: '""',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: item.color,
                    ml: 0.75,
                    mr: -0.25,
                  },
                }}
              />
            ))}
            {legend.length > 24 && (
              <Typography variant="caption" color="text.disabled">
                +{legend.length - 24} more
              </Typography>
            )}
          </Box>
        )}
      </Stack>
  );
};

const StyleEditor: React.FC<{
  view: ReferenceLayerView;
  onChange: (patch: Partial<ReferenceLayerStyle>) => void;
  onReset: () => void;
}> = ({ view, onChange, onReset }) => (
  <Box sx={{ pl: 1, pr: 0.5, pb: 1, pt: 0.5 }}>
    <ReferenceStyleControls
      geometryKind={view.layer.geometry_kind}
      fields={view.layer.fields}
      labelField={view.layer.label_field}
      style={view.style}
      onChange={onChange}
    />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
      <Button size="small" startIcon={<ResetIcon />} onClick={onReset} disabled={!view.hasOverride} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
        Use admin style
      </Button>
      {view.hasOverride && (
        <Typography variant="caption" color="text.disabled">
          Your overrides are saved in this browser
        </Typography>
      )}
    </Box>
  </Box>
);

/**
 * "Reference layers" block for the map settings dialogs: one row per admin
 * layer with an on/off switch, counts, zoom-to, and an expandable style editor
 * (colours, opacity, outline, colour ramp by property, labels).
 */
const ReferenceLayersSection: React.FC<ReferenceLayersSectionProps> = ({ controller, onZoomTo, hideTitle, isAdmin }) => {
  const { layers, listLoading, listError, enabledCount, setEnabled, setAllEnabled, setStyleOverride, resetStyle, retry } = controller;
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <Box>
      {!hideTitle && (
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4 }}>
            REFERENCE LAYERS
            {layers.length > 0 && (
              <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                ({enabledCount}/{layers.length} on)
              </Typography>
            )}
          </Typography>
          {layers.length > 1 && (
            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
              <Button size="small" onClick={() => setAllEnabled(true)} sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.66rem', textTransform: 'none' }}>
                All
              </Button>
              <Button size="small" onClick={() => setAllEnabled(false)} sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: '0.66rem', textTransform: 'none' }}>
                None
              </Button>
            </Box>
          )}
        </Stack>
      )}

      {listLoading && layers.length === 0 && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Loading layers…
          </Typography>
        </Stack>
      )}
      {listError && (
        <Typography variant="caption" color="error">
          {listError}
        </Typography>
      )}
      {!listLoading && !listError && layers.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No reference layers yet.{isAdmin ? ' Upload boundaries, districts or infrastructure in Settings → Reference Layers.' : ''}
        </Typography>
      )}

      <Stack spacing={0.25}>
        {layers.map((view) => {
          const { layer, style, enabled, loading, error } = view;
          const open = openId === layer.id;
          return (
            <Box
              key={layer.id}
              sx={{
                borderRadius: 1,
                border: '1px solid',
                borderColor: open ? 'divider' : 'transparent',
                bgcolor: open ? 'action.hover' : 'transparent',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ pl: 0.25, pr: 0.25, minHeight: 34 }}>
                <Switch size="small" checked={enabled} onChange={(e) => setEnabled(layer.id, e.target.checked)} inputProps={{ 'aria-label': `Show ${layer.name}` }} />
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: layer.geometry_kind === 'point' ? '50%' : '3px',
                    bgcolor: style.color,
                    opacity: Math.max(0.35, layer.geometry_kind === 'point' ? style.point_opacity : style.fill_opacity + 0.3),
                    border: `2px solid ${style.stroke_color}`,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {layer.name}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.2, display: 'block' }}>
                    {REFERENCE_LAYER_TYPE_LABELS[layer.layer_type] || layer.layer_type} · {geometryKindLabel(layer.geometry_kind)} ·{' '}
                    {layer.feature_count.toLocaleString()}
                    {loading ? ' · loading…' : ''}
                    {error ? ` · ${error}` : ''}
                  </Typography>
                </Box>
                {error && (
                  <Button size="small" onClick={() => retry(layer.id)} sx={{ minWidth: 0, px: 0.5, fontSize: '0.66rem', textTransform: 'none' }}>
                    Retry
                  </Button>
                )}
                {loading && <CircularProgress size={12} />}
                {onZoomTo && layer.bbox && (
                  <Tooltip title="Zoom to layer">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!enabled}
                        onClick={() => {
                          if (!enabled) return;
                          onZoomTo(layer.bbox as [number, number, number, number]);
                        }}
                        aria-label={`Zoom to ${layer.name}`}
                      >
                        <ZoomIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title={open ? 'Hide style' : 'Style: colour, opacity, outline, colour ramp, labels'}>
                  <IconButton
                    size="small"
                    onClick={() => setOpenId(open ? null : layer.id)}
                    aria-expanded={open}
                    aria-label={`Style ${layer.name}`}
                    sx={{ color: view.hasOverride ? 'primary.main' : undefined }}
                  >
                    {open ? <ExpandIcon sx={{ fontSize: 18 }} /> : <StyleIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              </Stack>
              <Collapse in={open} unmountOnExit>
                <StyleEditor view={view} onChange={(patch) => setStyleOverride(layer.id, patch)} onReset={() => resetStyle(layer.id)} />
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ReferenceLayersSection;
